/* eslint-disable no-undef */
import React from 'react';
import PropTypes from 'prop-types';
import { withGoogleMap, GoogleMap, Marker, DirectionsRenderer, InfoWindow } from "react-google-maps";
import {toastr} from 'react-redux-toastr';
import { Link } from 'react-router-dom';
import {BASE_URL, isNullEmptyOrWhitespace} from '../../constants';
import Pickup from './Pickup';
import PickupLite from './PickupLite';
import RunSummaryView from './RunSummaryView';
import RunEditView from './RunEditView';
import scrollToComponent from '../../assets/libs/custom-react-scroll-to-component';
import moment from 'moment';
import {delay, every} from 'lodash';
import {apiRequest} from '../../utils/request';
import FileSaver from "file-saver";
import {loadRun} from "../../actions/runActions";
import TankChecklistView from "../DriverRuns/TankChecklistView";
import RunReceiptsList from "../Common/RunReceiptsList";
import {getRunTypeCardStyle, isPickupUrgentForRunType} from "../../utils/collectionTypes";
import { mustBeArray } from '../../utils/lib';

const DirectionsGoogleMap = withGoogleMap(props => (
  <GoogleMap
    ref={props.onMapLoad}
    defaultZoom={15}
    center={props.center}
    zoom={props.zoom}
    options={{fullscreenControl:true}}
  >
    {props.directions && <DirectionsRenderer
      directions={props.directions}
      options={{ suppressMarkers: true }}
    />}
    {props.markers && props.markers.map((marker, index) => (
      <Marker key={marker.key}
        {...marker}
        onClick={() => props.onMarkerClick(marker)}
      >
        {marker.showInfo && (
          <InfoWindow onCloseClick={() => props.onMarkerClose(marker)}>
            <div className="jumbotron">{marker.infoContent}</div>
          </InfoWindow>
        )}
      </Marker>
    ))}
  </GoogleMap>
));


class Run extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      unfinishedRun: null,
      offset: 0,
      run: {},
      showEdit: false,
      viewCompletedRun: false
    };

    this.setMapLocation = this.setMapLocation.bind(this);
    this.resetMap = this.resetMap.bind(this);
    this.handleMapLoad = this.handleMapLoad.bind(this);
    this.handleMarkerClick = this.handleMarkerClick.bind(this);
    this.handleMarkerClose = this.handleMarkerClose.bind(this);
    this.setDirectionService = this.setDirectionService.bind(this);
    this.setupRun = this.setupRun.bind(this);
    this.setMapRoute = this.setMapRoute.bind(this);
    this.pickupView = this.pickupView.bind(this);
    this.onCancelReason = this.onCancelReason.bind(this);
    this.handleRunChange = this.handleRunChange.bind(this);
    this.onInputTank = this.onInputTank.bind(this);
    this.onInputTin = this.onInputTin.bind(this);
    this.focusMap = this.focusMap.bind(this);
    this.scrollToStart = this.scrollToStart.bind(this);
    this.showDriverLocations = this.showDriverLocations.bind(this);
    this.setupViewMode = this.setupViewMode.bind(this);
    this.handleViewMode = this.handleViewMode.bind(this);
    this.handleOnEditFieldChanged = this.handleOnEditFieldChanged.bind(this);
    this.onCompletePickup = this.onCompletePickup.bind(this);
    this.onPrintPdf = this.onPrintPdf.bind(this);
    this.scrollToRef = this.scrollToRef.bind(this);
    this.onSelectReceipt = this.onSelectReceipt.bind(this);
  }

  // Selecting a receipt fills the tank's litres, backfills the receipt's run/pickup/tank
  // association in the cloud DB, and ticks the tank — exactly as if the driver had typed the
  // value and confirmed it (mirrors onInputTank + the green tick, done atomically).
  onSelectReceipt(receipt, pickup, tank) {
    let run = {...this.state.run};
    let updatedPickup = null;
    run.pickups = [...this.state.run.pickups].map(p => {
      let returnP = {...p};
      if (returnP.id === pickup.id) {
        returnP.pickupTanks = [...returnP.pickupTanks].map(t => {
          let returnTank = {...t};
          if (t.tankID === tank.tankID) {
            returnTank.driverEstimatedLitresPickedUp = receipt.deliveryTotal;
          }
          return returnTank;
        });
        updatedPickup = returnP;
      }
      return returnP;
    });

    this.setState({selectedPickup: null, run}, () => {
      if (this.props.onAssignReceipt) {
        this.props.onAssignReceipt(receipt.id, pickup.id, tank.tankID);
      }
      this.props.onCompletePickup({...updatedPickup, pickedupTankId: tank.tankID});
    });
  }

  componentDidMount(){
    if(this.props.run.id){
      let {run, origin, destination, waypoints, markers} = this.setupRun({...this.props.run});
      this.setState({
        run: run,
        origin: origin.latLng,
        destination: destination.latLng,
        waypoints: waypoints,
        markers: markers,
        center: origin.latLng,
        zoom: 15,
        map: undefined,
        lastMarker: null,
        cancelReasons: [],
        showMap: false,
        unfinishedRun: this.props.unfinishedRun,
        showDriverLocations: this.props.showDriverLocations,
        isOrderingPickups: false,
        isMapView: this.props.isMapView,
        isListView: this.props.isListView,
        isEditView: false,
        selectedPickup: null,
        offset: 0,
      });
    } else {
      this.setState({
        run: {},
        isMapView: this.props.isMapView,
        isListView: this.props.isListView,
        isEditView: false,
        selectedPickup: null,
        offset: 0,
      });
    }

  }  

  componentWillReceiveProps(nextProps){
    if(!nextProps.run || !nextProps.run.id){
      this.setState({run: {}, loadingDriverLocations: false,});
      return;
    }
    if(mustBeArray(nextProps.run && nextProps.run.pickups).length !== mustBeArray(this.props.run && this.props.run.pickups).length){
      this.setState({run: nextProps.run});
    }

    if(nextProps.run.id !== this.state.run.id){
      let {run, origin, destination, waypoints, markers} = this.setupRun({...nextProps.run});

      this.setState({
        run: run,
        origin: origin.latLng,
        destination: destination.latLng,
        waypoints: waypoints,
        markers: markers,
        center: origin.latLng,
        zoom: 15,
        map: undefined,
        lastMarker: null,
        cancelReasons: [],
        showMap: false,
        unfinishedRun: nextProps.unfinishedRun,
        showDriverLocations: nextProps.showDriverLocations,
        isMapView: nextProps.isMapView,
        isListView: nextProps.isListView,
        loadingDriverLocations: false,
      });
      this.scrollToStart();
    }
    else if(!this.state.isEditView){
      let {selectedPickup, isListView, isMapView} = this.state;

      for (let i = 0; i < nextProps.run.pickups.length; i++) {
        let newItem = nextProps.run.pickups[i];
        let current = this.state.run.pickups.filter(x => x.id === newItem.id);
        if (current.length === 1 && current[0].isUrgentJob !== newItem.isUrgentJob) {
          toastr.confirm(`Job urgency changed for ${newItem.clientCompanyName}`, {
            timeOut: 0,
            onOk: () => {
              if(isListView){
                this.scrollToRef(`pickup-${newItem.id}`);
              } else if(isMapView){
                this.setMapLocation(newItem, null,'pickup');
              }
            },
            okText: 'Show updated job',
          });
        } else if (current.length === 0) {
          toastr.confirm(`New job ${newItem.clientCompanyName} added to run`, {
            timeOut: 0,
            onOk: () => {
              if(isListView){
                this.scrollToRef(`pickup-${newItem.id}`);
              } else if(isMapView){
                this.setMapLocation(newItem, null,'pickup');
              }
            },
            okText: 'Show new job',
          });
        }
      }

      let pickups = [...nextProps.run.pickups.map(p => {
        let returnVal = p;
        return selectedPickup && selectedPickup.id === p.id ? this.state.run.pickups.filter(y => y.id === selectedPickup.id)[0] : returnVal;
      })];
      this.handleRunChange({...nextProps.run, pickups: pickups}, nextProps.unfinishedRun);
    }
  }

  scrollToRef =(ref) =>{
    let element = this.refs[ref];
    let modal = $('.modal.show')[0];
    if (modal) {
      //console.log(element.offsetTop, modal.offsetHeight);
      const elementOffsetTop = (element && element.offsetTop) || 0;
      const modalOffsetTop = (modal && modal.offsetTop) || 0;
      modal.scrollTop = (elementOffsetTop + modalOffsetTop) + 150;
    } else {
      scrollToComponent(element, {
        offset: -70,
        align: 'top',
        duration: 1500
      });
    }
  }

  scrollToStart(){

    if(this.state.unfinishedRun){
      window.scrollTo(0,0);
    }
    else if(this.state.run.id) {
      if (this.state.isListView) {
        //find the first pending pickup and scroll to it
        for (let i = 0; i < this.state.run.pickups.length; i++) {
          if (this.props.scrollToLocationId > 0) {
            if (this.state.run.pickups[i].locationID === this.props.scrollToLocationId) {
              this.scrollToRef(`pickup-${this.state.run.pickups[i].id}`);
              break;
            }
          } else {
            if (this.state.run.pickups[i].pickupStatusCode === 'PEN') {
              this.scrollToRef(`pickup-${this.state.run.pickups[i].id}`);
              break;
            }
          }
        }
      }
    }
  }

  handleRunChange(nextRun, unfinishedRun){
    let {markers} = this.setupRun(nextRun);
    if(this.state.lastMarker){
      let current = markers.filter(x => x.key === this.state.lastMarker.key)[0];
      let exMarkers = markers.map(x => {
        let returnMarker = {...x};
        if(returnMarker.key === this.state.lastMarker.key){
          current.showInfo = true;
          return current;
        }
        return returnMarker;
      });
      this.setState({run: nextRun, markers: exMarkers, unfinishedRun: unfinishedRun, loadingDriverLocations: false, isEditView: this.state.isEditView});
    } else {
      this.setState({run: nextRun, unfinishedRun: unfinishedRun, loadingDriverLocations: false, isEditView: this.state.isEditView});
    }
  }

  setupRun(run){
    let locationSet = [];
    let pickups = [...run.pickups];
    let locations =  pickups.map(x => {
      let latLng = {lat: x.locationLatitude, lng: x.locationLongitude};
      if(locationSet.filter(x => x.lat === latLng.lat && x.lng === latLng.lng).length > 0){
        latLng = {lat: latLng.lat, lng: latLng.lng + 0.0001};
      }else{
        locationSet.push(latLng);
      }
      return {pickup: x, latLng: latLng, driverLocation: {id: null}, key: `pickup_${x.id}`};
    });
    let origin = locations[0];
    let destination = locations[locations.length-1];

    let waypoints = [...locations.filter(x => x.pickup.id !== origin.pickup.id && destination.pickup.id !== x.pickup.id)
      .map(x => {
        return {location: x.latLng};
      })];

    waypoints = [...waypoints];

    /* Add driver locations*/

    for(let i = 0; i<run.driverLocations.length;i++) {
      let latLng = {
        lat: run.driverLocations[i].latitude,
        lng: run.driverLocations[i].longitude
      };

      let filtered = locations.filter(x => x.latLng.lat === latLng.lat && x.latLng.lng === latLng.lng);
      while(filtered.length > 0){
        latLng = {lat: latLng.lat, lng: latLng.lng + 0.0001};
        filtered = locations.filter(x => x.latLng.lat === latLng.lat && x.latLng.lng === latLng.lng);
      }

      locations.push({
        latLng: latLng,
        driverLocation: run.driverLocations[i],
        pickup: {id:null},
        key: `driverLocation_${run.driverLocations[i].id}`,
      });
    }

    /* end driver locations */

    let markers = locations.map(x => {
      if(x.pickup && x.pickup.id){
        let colour = 'green-dot';
        if(x.pickup.pickupStatusCode === 'PEN'){
          colour = 'orange-dot';
        }
        if(x.pickup.pickupStatusCode === 'CAN'){
          colour = 'red-dot';
        }
        if(x.pickup.isCheckIn && x.pickup.pickupStatusCode !== 'COM'){
          colour = 'blue-dot';
        }
        if(isPickupUrgentForRunType(x.pickup, run && run.truckCollectionTypeCode) && x.pickup.pickupStatusCode !== 'COM'){
          colour = 'red-dot';
        }
        if(x.pickup.id === -1 || this.props.scrollToLocationId === x.pickup.locationID){
          colour = 'flag';
        }

        return {
          location: x,
          position: x.latLng,
          key: x.key,
          showInfo: x.pickup.id === -1 ? true : false,
          infoContent: this.pickupView(x.pickup, run),
          icon: `//maps.google.com/mapfiles/ms/icons/${colour}.png`
        };
      } else if(x.driverLocation && x.driverLocation.id){
        return {
          location: x,
          position: x.latLng,
          key: x.key,
          showInfo: false,
          infoContent: (<div className="container" style={{marginLeft: null, paddingLeft: null, paddingRight: null}}>
            <div className="row">
              <div className="col-md-12 col-xs-12">
                <h3>Recorded driver location</h3>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-xs-12">
                <strong>Driver: </strong>{x.driverLocation.driverFullName}
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-xs-12">
                <strong>Pickup: </strong>{x.driverLocation.pickupLocationName ? x.driverLocation.pickupLocationName : 'Last known driver location'}
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-xs-12">
                <strong>Date: </strong>{moment(x.driverLocation.createdDate).format("dddd, MMMM Do YYYY, h:mm:ss a")}
              </div>
            </div>
          </div>),
          icon: x.driverLocation.pickupLocationName ? `//maps.google.com/mapfiles/ms/icons/pink-dot.png` : `//maps.google.com/mapfiles/ms/icons/truck.png`
        };
      }
    });

    return {
      run,
      pickups,
      locations,
      origin,
      destination,
      waypoints,
      markers
    };
  }

  pickupView(pickup, run) {
    let isReadOnly = (!this.state || (this.state.unfinishedRun !== null && this.state.unfinishedRun !== undefined) || (this.state.run && !this.state.run.isCurrentUserDriver)) && !this.props.isAdmin;
    let isPickupLite = this.props.isPickupLite;
    return isPickupLite ?
      (<PickupLite
        pickup={pickup}
        onMapLocation={this.setMapLocation}
        onResetRoute={this.resetMap}
        run={run}
      />) : (
      <Pickup
        pickup={pickup}
        onMapLocation={this.setMapLocation}
        onMapRoute={(location, evnt)=> this.setMapRoute(location, evnt)}
        onLocationCheckin={this.props.onLocationCheckin}
        onResetRoute={this.resetMap}
        onCancelReason={this.onCancelReason}
        onInputTank={this.onInputTank}
        onInputTin={this.onInputTin}
        run={run}
        isReadOnly={isReadOnly}
        isAdmin={this.props.isAdmin}
        onFinishSkipEdit={this.props.onSaveReorderRun}
        showImages={this.props.showPickupImages}
        selected={(this.state && this.state.selectedPickup && this.state.selectedPickup.id === pickup.id) ? true : false}
        onStartPickup={(p) => {
          this.setState({selectedPickup: p}, ()=> this.handleRunChange(run));
        }}
        onCompletePickup={this.onCompletePickup}
        onCancelPickup={(_) => {
          this.setState({selectedPickup: null}, () => {
            this.handleRunChange(run);
            /*if (this.props.onRunRefresh) {
              this.props.onRunRefresh();
            }*/
          });
        }}
        tankId={this.props.tankId}
        unassignedReceipts={this.props.unassignedReceipts}
        allReceipts={this.props.allReceipts}
        receiptsLoading={this.props.receiptsLoading}
        onSelectReceipt={this.onSelectReceipt}
        onUnassignReceipt={this.props.onUnassignReceipt}
        onRefreshReceipts={this.props.onRefreshReceipts}
      />);
  }

  onInputTank(evnt, runId, pickupId, tankId){
    evnt.preventDefault();
    //This is being recorded in state, but the markers aren't re-drawn
    let run = {...this.state.run};
    run.pickups = [...this.state.run.pickups].map(p => {
      let returnP = {...p};
      if (returnP.id === pickupId) {
        let tanks = [...returnP.pickupTanks].map(t => {
          let returnTank = {...t};
          if(t.id === tankId) {
              const regex = /([0-9]*[\.|\,]{0,1}[0-9]{0,2})/s;
              returnTank.driverEstimatedLitresPickedUp = evnt.target.value.match(regex)[0];
          }
          return returnTank;
        });
        returnP.pickupTanks = tanks;
      }
      return returnP;
    });

    this.handleRunChange(run);
  }

  onCompletePickup(pickup, isCancelled  = false) {
    if(isCancelled && isNullEmptyOrWhitespace(pickup.cancelReason)){
      toastr.warning('Please enter a cancellation reason');
    } else {
      this.setState({selectedPickup: null}, () => this.props.onCompletePickup(pickup, isCancelled));
    }
    /*
    else if(isCancelled){
      this.setState({selectedPickup: null}, () => this.props.onCompletePickup(pickup, isCancelled));
    } else if (pickup.isTinLocation) {
      let tins = pickup.numberOfTinsCollected;
      tins = parseInt(tins);
      if (tins >= 0) {
        this.setState({selectedPickup: null}, () => this.props.onCompletePickup(pickup, isCancelled));
      } else {
        toastr.warning(`Tins must be greater than or equal to zero to pickup`);
      }
    } else {
      let ok = false;
      if(pickup.pickedupTankId){
        let tank = pickup.pickupTanks.find(x => x.tankID === pickup.pickedupTankId);
        ok = tank && parseInt(tank.driverEstimatedLitresPickedUp) >= 0;
      } else {
        ok =  every(pickup.pickupTanks, t => {
          let pickupLitres = parseInt(t.driverEstimatedLitresPickedUp);
          return pickupLitres >= 0;
        });
      }
      if (!ok) {
        toastr.warning(`All tanks must be greater than or equal to zero to pickup`);
      } else {
        this.setState({selectedPickup: null}, () => this.props.onCompletePickup(pickup, isCancelled));
      }
    }*/
  }

  onCancelReason(evnt, pickupId, reason = null){
    evnt.preventDefault();
    let run = {...this.state.run};
    run.pickups = [...this.state.run.pickups].map(p => {
        let returnP = {...p};
        if (returnP.id === pickupId) {
          returnP.cancelReason = reason === null ? evnt.target.value : reason;
        }
        return returnP;
      });

    this.handleRunChange(run);
  }

  onInputTin(evnt, runId, pickupId){
    evnt.preventDefault();
    let run = {...this.state.run};
    run.pickups = [...this.state.run.pickups].map(p => {
      let returnP = {...p};
      if (returnP.id === pickupId) {
        returnP.numberOfTinsCollected = evnt.target.value;
      }
      return returnP;
    });

    this.handleRunChange(run);
  }

  setDirectionService(origin, destination, waypoints, markers, currentMarket = null){
    /*
    Solution to ove 23 locations, will require being called at an intelligent time to re-order the run
    if(this.state.map){
      var myOptions = {
        zoom: 20,
        center: origin,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      };
      let myMap = new google.maps.Map(this.refs.test2, myOptions);

      let tsp = new BpTspSolver(myMap, this.refs.test);
      tsp.startOver();
      tsp.setTravelMode(google.maps.DirectionsTravelMode.DRIVING);
      console.log('solving');
      for(let i=0;i<waypoints.length;i++){
        tsp.addWaypoint({...waypoints[i].location}, ()=>{});
      }
      tsp.solveAtoZ((x)=>{
        console.log('solved', x.getOrder());
        console.log(this.refs.test);
        //console.log('solved', this.state.tsp.getGDirections(), x, this.state.tsp.getOrder());
      });
    }*/
    if(waypoints.length > 23){
      console.log(`Run contains more than 23 locations, can't show full route`);
      //toastr.warning(`Run contains more than 23 locations, can't show full route`);
    }
    waypoints = [...waypoints].slice(0,23);

    const DirectionsService = new google.maps.DirectionsService();
    DirectionsService.route({
      origin: origin,
      destination: destination,
      waypoints: waypoints,
      optimizeWaypoints: false,
      travelMode: google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === google.maps.DirectionsStatus.OK) {
        this.setState({
          origin: origin,
          destination: destination,
          waypoints: waypoints,
          directions: result,
          markers: markers,
          lastMarker: currentMarket
        });
      } else {
        toastr.error(`Error fetching directions ${result}`);
      }
    });
  }

  setMapRoute(location, evnt) {
    evnt.preventDefault();

    //get the previous location
    //set the waypoints and bounding box
    let previous = null;
    let { markers } = this.setupRun(this.state.run);
    for(let i=0; i < markers.length; i++){
      if(markers[i].location.pickup.id === location.id && i > 0){
        previous = markers[i-1];
        break;
      }
    }

    let position = {
      lat: location.locationLatitude,
      lng: location.locationLongitude
    };
    if(!previous){
      previous = {position: position};
      position = this.state.markers[1].position;
    }
    this.focusMap();

    let newMarkers = markers.filter(x =>
    (x.position.lat === previous.position.lat && x.position.lng === previous.position.lng)
    || (x.position.lat === position.lat && x.position.lng === position.lng))
      .map(x => {
        if(x.pickup.id === location.id){
          x.showInfo = true;
        }
        return x;
      });
    let currentMarker = newMarkers.filter(x => x.pickup.id === location.id)[0];
    this.setDirectionService(previous.position, position, [], newMarkers, currentMarker);
  }

  focusMap(){
    this.refs.map.classList.remove('collapse');
    this.setState({isMapView: true});
    if(!$('.modal.show')[0]){
      scrollToComponent(this.refs.map, {
        align: 'top',
        duration: 1500
      });
    }
  }

  setMapLocation(location, evnt, keySuffix = 'pickup') {
    if(evnt){
      evnt.preventDefault();
    }
    //if marker isn't on the map, then add it and zoom to that area
    let { markers } = this.setupRun(this.state.run);
    let currentMarker = markers.filter(x => x.key === `${keySuffix}_${location.id}`)[0];
    let existing = markers.filter(x => x.key === currentMarker.key);
    let newMarkers = markers;
    if(existing.length === 0){
      newMarkers.push(currentMarker);
    }

    let bounds = new google.maps.LatLngBounds();

    bounds.extend(currentMarker.position);
    bounds.extend({lat: currentMarker.position.lat - 0.001, lng: currentMarker.position.lng});
    bounds.extend({lat: currentMarker.position.lat + 0.001, lng: currentMarker.position.lng});

    let xMarkers = newMarkers.map(x => {
      if(x.key === `${keySuffix}_${location.id}`){
        x.showInfo = true;
      }
      return x;
    });
    this.focusMap();
    this.setState({
      markers: xMarkers,
      lastMarker: xMarkers.filter(x => x.key === `${keySuffix}_${location.id}`)[0],
      center: currentMarker.position,
      zoom: 16
    });
    if(this.state.map && this.state.map.context){
      google.maps.event.trigger(this.state.map.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED, 'resize');
    }
    //this.state.map.fitBounds(bounds);
  }

  resetMap(evnt, doFocus = true){
    if(evnt){
      evnt.preventDefault();
    }
    let {origin, destination, waypoints, markers} = this.setupRun(this.state.run);

    if(doFocus) {
      this.focusMap();
    }

    this.setDirectionService(origin.latLng, destination.latLng, waypoints, markers);

    if(this.state.map && this.state.map.context){
      google.maps.event.trigger(this.state.map.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED, 'resize');
    }
  }

  handleMapLoad(map){
    if(map){
      this.setState({map: map});
      if(this.state.showDriverLocations) {
        this.showDriverLocations();
      } else {
        this.setupViewMode();
        this.resetMap(null, false);
      }
    }
  }

  handleMarkerClick(targetMarker) {
    this.setState({
      markers: this.state.markers.map(marker => {
        marker.showInfo = false;
        if (marker === targetMarker) {
          return {
            ...marker,
            showInfo: true,
          };
        }
        return marker;
      }),
      lastMarker: targetMarker,
    });
  }

  handleMarkerClose(targetMarker) {
    this.setState({
      markers: this.state.markers.map(marker => {
        if (marker === targetMarker) {
          return {
            ...marker,
            showInfo: false,
          };
        }
        return marker;
      }),
      lastMarker: null,
    });
  }

  showDriverLocations(){
    this.resetMap();
  }

  handleViewMode(view, checked){
    let newState = {
      isListView: view === 'list' ? checked : this.state.isListView,
      isMapView: view === 'map' ? checked : this.state.isMapView,
      isEditView: view === 'edit' ? checked : this.state.isEditView,
    };
    
    if(newState.isEditView){
      newState.isListView = false;
      newState.isMapView = false;
      if(this.refs.locations) {
        this.refs.locations.classList.add('collapse');
      }
      if (this.refs.map) {
        this.refs.map.classList.add('collapse');
      }
    }

    if(view === 'list'){
      if(newState.isListView && this.refs.locations){
        this.refs.locations.classList.remove('collapse');
      } else if(this.refs.locations) {
        this.refs.locations.classList.add('collapse');
      }
    }
    if(view === 'map') {
      if (newState.isMapView && this.refs.map) {
        this.resetMap();
      } else if (this.refs.map) {
        this.refs.map.classList.add('collapse');
      }
    }

    this.setState(newState);
  }

  setupViewMode() {
    if (this.state.isListView && this.refs.locations) {
      this.refs.locations.classList.remove('collapse');
    } else if (this.refs.locations) {
      this.refs.locations.classList.add('collapse');
    }

    if (this.state.isMapView && this.refs.map) {
      this.refs.map.classList.remove('collapse');
    } else if (this.refs.map) {
      this.refs.map.classList.add('collapse');
    }
    this.scrollToStart();
  }

  onPrintPdf(e){
    e.preventDefault();
    toastr.info('Creating PDF labels...');
    apiRequest(`/api/labels?runid=${this.state.run.id}&offset=${this.state.offset}`, 'GET', false)
      .then(x => {
        toastr.success('Created PDF labels!');
        x.data.blob().then(blob => {
            FileSaver.saveAs(blob, `${this.state.run.id}_labels.pdf`);
          });
      });
  }

  handleOnEditFieldChanged(e){
    let run = {...this.state.run};
    run[e.target.name] = e.target.value;
    this.setState({run});
  }
  handleViewCompletedRun= () => {
    this.setState({ viewCompletedRun: !this.state.viewCompletedRun});
  }
  render(){
    let {run, unfinishedRun} = this.state;
    const {showLocationsWithNotes, locationId, viewMode} = this.props;
    const pickupList =  mustBeArray(showLocationsWithNotes ? _.filter(run && run.pickups, 
      o => o.locationID === locationId) :  run.pickups);
    if(!run.id){
      return (<div>&nbsp;</div>);
    }
    return (<div id={run.id} ref="unfinishedRun">
        { unfinishedRun && !showLocationsWithNotes && <div className="row">
          <div className="col-md-12">
            <div className="card" key={run.regionsVisited}>
              <div className="card-block">
                <h5>
                  <span className="badge badge-danger">Run {unfinishedRun.id} is incomplete</span>
                </h5>
                <h4>{unfinishedRun.runDate}</h4>
                <h4 className="card-title"><Link to={`${BASE_URL}/runs/driver/${unfinishedRun.id}`} className="nav-link">{unfinishedRun.regionsVisited} - {unfinishedRun.truckName}</Link></h4>
                <h6>This run must be completed before another run can be started</h6>
              </div>
          </div>
          </div>
        </div>}
        {
          !showLocationsWithNotes &&
          <div className="row">
          <div className="col-md-12">
            <div className="card" key={run.regionsVisited} style={getRunTypeCardStyle(run.truckCollectionTypeCode)}>
              {!this.state.isEditView &&
              <RunSummaryView
                run={run}
                onRunRefresh={() => this.props.loadRun(this.props.runId, null, null, false, true)}
                isListView={this.state.isListView}
                isMapView={this.state.isMapView}
                handleViewMode={this.handleViewMode}
                isAdmin={this.props.isAdmin}
                onFinishOrderRun={this.props.onSaveRun}
                resetMap={this.resetMap}
                onEnterReOrderRun={() => {
                  this.handleViewMode('edit', true);
                }}
                onLoadDriverLocations={()=> {
                  this.setState({loadingDriverLocations: true});
                  this.props.onLoadDriverLocations();
                }}
                scrollToComponent={scrollToComponent}
                driverLocations={this.refs.driverLocations}
                showEdit={this.props.showEdit}
                onPrintPdf={this.onPrintPdf}
                offset={this.state.offset}
                onChangeOffset={(e) => this.setState({offset: parseInt(e.target.value)})}
                isEditView={false}
                handleViewCompletedRun={this.handleViewCompletedRun}
                viewCompletedRun={this.state.viewCompletedRun}
              />}
              {this.state.isEditView &&
              <RunEditView
                run={run}
                drivers={this.props.drivers}
                trucks={this.props.trucks}
                onEditFieldChange={this.handleOnEditFieldChanged}
                handleViewMode={this.handleViewMode}
                onFinishEdit={(run) => {
                  this.setState({isEditView: false}, () => this.props.onSaveRun(run));
                }}
                onDeleteRun={this.props.onDeleteRun}
                isAdmin={this.props.isAdmin}
                isEditView={true}
                clearEditView = {() => {
                  this.handleViewMode('edit', false);
                }}
                loadRun={this.props.loadRun}

              /> }
            </div>
          </div>
        </div>
        }
        
      {
        !showLocationsWithNotes &&
        <TankChecklistView
            runId={run.id}
            baseLocationIds={run.pickups.filter(x => x.isBase).map(x => x.locationID)}
            onUpdate={() => this.props.dispatch(loadRun(run.id, null, null, false, true))}
            showCollapsed={!run.hasDropOffTanks}
        />
      }
      
        <div className="row" ref="locations">
          <div className="col-md-12" ref="pickups">
            <div className="card" key={run.regionsVisited}>
              <ul className="list-group list-group-flush">
                {pickupList.map((p) => {
                  return (!p.smsNoResponse && <div className="list-group-item" ref={`pickup-${p.id}`} key={p.id}>{this.pickupView(p, run)}</div>);
                })}
              </ul>
            </div>
          </div>
        </div>

      { !showLocationsWithNotes && run.id &&
        <div className="row">
          <div className="col-md-12">
            <RunReceiptsList runId={run.id} />
          </div>
        </div>
      }

      { (run.driverLocations.length > 0 || this.state.loadingDriverLocations) && !showLocationsWithNotes &&
      <div className="row" ref="driverLocations" style={{marginBottom: '25px'}}>
        <div className="col-md-12">
          <h5>Recorded driver pickups { this.state.loadingDriverLocations && <span className="text-muted">Loading... <i className="fa fa-cog fa-spin" /></span>}</h5>
          <ul className="list-group">
            {run.driverLocations.map(d => {
              return (<li key={d.id} className="list-group-item">
                <a href="#"
                  onClick={(evnt) => this.setMapLocation(d, evnt, 'driverLocation')}>{moment(d.createdDate).format("dddd, MMMM Do YYYY, h:mm:ss a")} - {d.pickupLocationName ? d.pickupLocationName : 'Truck location'} - {d.driverFullName}</a></li>);
            })}
          </ul>
        </div>
      </div>
      }
      {
        !showLocationsWithNotes &&
        <div className="row" ref="map">
          <div className="col-md-12">
            <DirectionsGoogleMap
              loadingElement={<div style={{ height: `100%` }} />}
              containerElement={<div style={{ height: `800px` }} />}
              mapElement={<div style={{ height: `100%` }} />}

              /*containerElement={
                <div style={{height: `800px`, maxWidth:'95%'}}/>
              }
              mapElement={
                <div style={{height: `800px`, maxWidth:'95%'}}/>
              }*/
              onMapLoad={this.handleMapLoad}
              center={this.state.center}
              zoom={this.state.zoom}
              directions={this.state.directions}
              markers={this.state.markers}
              onMarkerClick={this.handleMarkerClick}
              onMarkerClose={this.handleMarkerClose}
            />
          </div>
        </div>
      }
        
      </div>);
  }
}
Run.propTypes = {
  onLocationCheckin: PropTypes.func,
  onRunRefresh: PropTypes.func,
  run: PropTypes.object.isRequired,
  unfinishedRun: PropTypes.object,
  showDriverLocations: PropTypes.bool,
  isPickupLite: PropTypes.bool,
  onSaveRun: PropTypes.func,
  onEnterReOrderRun: PropTypes.func,
  isMapView: PropTypes.bool.isRequired,
  isListView: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  scrollToLocationId: PropTypes.number,
  onLoadDriverLocations: PropTypes.func,
  trucks: PropTypes.object,
  drivers: PropTypes.object,
  showEdit: PropTypes.bool,
  onDeleteRun: PropTypes.func,
  onSaveReorderRun: PropTypes.func,
  showPickupImages: PropTypes.bool.isRequired,
  onCompletePickup: PropTypes.func,
  tankId: PropTypes.number,
};

Run.defaultProps = {
  isAdmin: false,
  showEdit: false,
  onDeleteRun: () => { console.log('Delete function not defined'); },
  showPickupImages: true,
};

export default Run;
